import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from '../../context/AuthContext';
import useFetchObjects from '../../api/useFetchObjects';
import { Download } from '@mui/icons-material';
import { exportComprehensivePurchaseReportToPDF } from '../../utils/exportToPDF';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function ComprehensivePurchaseReport({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  
  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [viewMode, setViewMode] = useState('simple');

  const queryString = useMemo(() => {
    const params = ["page_size=all"]; // Always fetch all records for reports
    if (startDate) params.push(`start_date=${startDate.format('YYYY-MM-DD')}`);
    if (endDate) params.push(`end_date=${endDate.format('YYYY-MM-DD')}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedSupplier) params.push(`supplier=${selectedSupplier}`);
    return `?${params.join('&')}`;
  }, [startDate, endDate, selectedBranch, selectedSupplier]);

  const { data: purchasesData, isLoading: purchasesLoading } = useFetchObjects(
    ['purchases', queryString], `shop/purchases/${queryString}`, token
  );
  const { data: paymentsData, isLoading: paymentsLoading } = useFetchObjects(
    ['purchase-payments', queryString], `shop/purchase-payments/${queryString}`, token
  );
  const { data: returnsData, isLoading: returnsLoading } = useFetchObjects(
    ['purchase-returns', queryString], `shop/purchase-returns/${queryString}`, token
  );
  const { data: suppliersData } = useFetchObjects(
    ['suppliers'], `shop/suppliers/`, token
  );

  const purchases = purchasesData?.results || purchasesData || [];
  const payments = paymentsData?.results || paymentsData || [];
  const returns = returnsData?.results || returnsData || [];
  const suppliers = suppliersData?.results || suppliersData || [];
  
  const isLoading = purchasesLoading || paymentsLoading || returnsLoading;

  const parseAmount = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null) {
      const currencies = Object.keys(value);
      if (currencies.length > 0) {
        return parseFloat(value[currencies[0]]) || 0;
      }
    }
    const numStr = String(value).replace(/[^0-9.-]/g, '');
    return parseFloat(numStr) || 0;
  };

  const summary = {
    total_purchases: {
      count: purchases.length,
      amount: purchases.reduce((sum, p) => sum + parseAmount(p.total_price), 0)
    },
    total_payments: {
      count: payments.length,
      amount: payments.reduce((sum, p) => sum + parseAmount(p.amount), 0)
    },
    total_returns: {
      count: returns.length,
      amount: returns.reduce((sum, r) => sum + parseAmount(r.total_refund_amount || (r.return_items?.[0]?.refund_amount)), 0)
    },
    total_paid: purchases.reduce((sum, p) => sum + parseAmount(p.total_paid), 0),
    total_remaining: purchases.reduce((sum, p) => sum + parseAmount(p.total_remained_amount), 0),
    outstanding_amount: purchases.reduce((sum, p) => sum + parseAmount(p.total_remained_amount), 0),
    payment_status: {
      paid: purchases.filter(p => parseAmount(p.total_remained_amount) === 0).length,
      partial: purchases.filter(p => parseAmount(p.total_remained_amount) > 0 && parseAmount(p.total_paid) > 0).length,
      unpaid: purchases.filter(p => parseAmount(p.total_paid) === 0).length
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const exportToPDF = () => {
    const exportData = {
      purchases,
      payments,
      returns,
      summary,
      suppliers
    };
    exportComprehensivePurchaseReportToPDF(exportData);
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedSupplier('');
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading purchase data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Comprehensive Purchase Report
      </Typography>

      {/* View Mode Toggle */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant={viewMode === 'simple' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('simple')}
          sx={{ mr: 1 }}
        >
          📊 Simple View
        </Button>
        <Button
          variant={viewMode === 'detailed' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('detailed')}
        >
          📋 Detailed View
        </Button>
      </Box>

      {viewMode === 'simple' ? (
        /* Simple View */
        <Box>
          <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
              📈 Quick Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {summary.total_purchases.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Total Purchases
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'white' }}>
                    {summary.total_purchases.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {summary.total_paid.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Total Paid
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white' }}>
                    {summary.total_payments.count} payments
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: 'warning.main', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {summary.outstanding_amount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Outstanding
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white' }}>
                    Remaining to Pay
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {summary.total_returns.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Returns
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'white' }}>
                    {summary.total_returns.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box sx={{ p: 2, bgcolor: 'secondary.main', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                    Payment Status
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block' }}>
                    ✅ Paid: {summary.payment_status.paid}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block' }}>
                    ⏳ Partial: {summary.payment_status.partial}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', display: 'block' }}>
                    ❌ Unpaid: {summary.payment_status.unpaid}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Recent Purchases</Typography>
            {purchases.slice(0, 5).map((purchase) => (
              <Box key={purchase.id} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 2, 
                mb: 1, 
                bgcolor: 'grey.50', 
                borderRadius: 1 
              }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    #{purchase.id} - {purchase.supplier_details?.name || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {purchase.purchase_date}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" color="primary">
                    {parseAmount(purchase.total_price).toFixed(2)}
                  </Typography>
                  <Chip 
                    label={parseAmount(purchase.total_remained_amount) === 0 ? '✅ Paid' : '⏳ Pending'}
                    size="small"
                    color={parseAmount(purchase.total_remained_amount) === 0 ? 'success' : 'warning'}
                  />
                </Box>
              </Box>
            ))}
          </Paper>
        </Box>
      ) : (
        /* Detailed View */
        <Box>
          {/* Comprehensive Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'primary.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">Total Purchases</Typography>
                  <Typography variant="h4" color="primary">{summary.total_purchases.count}</Typography>
                  <Typography variant="body2">Amount: {summary.total_purchases.amount.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'success.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">Total Paid</Typography>
                  <Typography variant="h4" color="success.main">{summary.total_paid.toFixed(2)}</Typography>
                  <Typography variant="body2">{summary.total_payments.count} payments</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'warning.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="warning.main">Outstanding</Typography>
                  <Typography variant="h4" color="warning.main">{summary.outstanding_amount.toFixed(2)}</Typography>
                  <Typography variant="body2">Remaining to Pay</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'info.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="info.main">Total Returns</Typography>
                  <Typography variant="h4" color="info.main">{summary.total_returns.count}</Typography>
                  <Typography variant="body2">Amount: {summary.total_returns.amount.toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Card sx={{ bgcolor: 'secondary.light' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="secondary.main">Payment Status</Typography>
                  <Typography variant="body2" color="success.main">✅ Paid: {summary.payment_status.paid}</Typography>
                  <Typography variant="body2" color="warning.main">⏳ Partial: {summary.payment_status.partial}</Typography>
                  <Typography variant="body2" color="error.main">❌ Unpaid: {summary.payment_status.unpaid}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Financial Summary */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>💰 Complete Financial Overview</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
                  <Typography variant="h6" color="primary" sx={{ mb: 1 }}>Purchase Summary</Typography>
                  <Typography variant="body1">Total Purchase Amount: <strong>{summary.total_purchases.amount.toFixed(2)}</strong></Typography>
                  <Typography variant="body1">Total Paid Amount: <strong>{summary.total_paid.toFixed(2)}</strong></Typography>
                  <Typography variant="body1">Outstanding Amount: <strong>{summary.outstanding_amount.toFixed(2)}</strong></Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Payment Progress: {summary.total_purchases.amount > 0 ? ((summary.total_paid / summary.total_purchases.amount) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '2px solid', borderColor: 'info.main', borderRadius: 2 }}>
                  <Typography variant="h6" color="info.main" sx={{ mb: 1 }}>Additional Information</Typography>
                  <Typography variant="body1">Total Returns: <strong>{summary.total_returns.amount.toFixed(2)}</strong></Typography>
                  <Typography variant="body1">Net Purchase Value: <strong>{(summary.total_purchases.amount - summary.total_returns.amount).toFixed(2)}</strong></Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Note: Returns don't affect outstanding amount as it represents final remaining balance
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select
                      value={selectedSupplier}
                      onChange={(e) => setSelectedSupplier(e.target.value)}
                      label="Supplier"
                    >
                      <MenuItem value="">All Suppliers</MenuItem>
                      {suppliers.map((supplier) => (
                        <MenuItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button 
                    variant="contained" 
                    onClick={exportToPDF} 
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          {/* Tabs */}
          <Paper sx={{ width: '100%' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`Purchases (${purchases.length})`} />
              <Tab label={`Payments (${payments.length})`} />
              <Tab label={`Returns (${returns.length})`} />
            </Tabs>

            {/* Purchase Tab */}
            <TabPanel value={tabValue} index={0}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Paid</TableCell>
                      <TableCell>Remaining</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchases.map((purchase) => (
                      <TableRow key={purchase.id}>
                        <TableCell>#{purchase.id}</TableCell>
                        <TableCell>{purchase.supplier_details?.name || 'N/A'}</TableCell>
                        <TableCell>{purchase.purchase_date}</TableCell>
                        <TableCell>{parseAmount(purchase.total_price).toFixed(2)}</TableCell>
                        <TableCell>{parseAmount(purchase.total_paid).toFixed(2)}</TableCell>
                        <TableCell>{parseAmount(purchase.total_remained_amount).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={parseAmount(purchase.total_remained_amount) === 0 ? 'Paid' : parseAmount(purchase.total_paid) > 0 ? 'Partial' : 'Unpaid'} 
                            size="small" 
                            color={parseAmount(purchase.total_remained_amount) === 0 ? 'success' : parseAmount(purchase.total_paid) > 0 ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: 'primary.light', fontWeight: 'bold' }}>
                      <TableCell colSpan={3}><strong>TOTAL ({purchases.length} purchases)</strong></TableCell>
                      <TableCell><strong>{summary.total_purchases.amount.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>{summary.total_paid.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>{summary.outstanding_amount.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>Outstanding</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Payment Tab */}
            <TabPanel value={tabValue} index={1}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Purchase</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Currency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>#{payment.id}</TableCell>
                        <TableCell>Purchase #{payment.purchase}</TableCell>
                        <TableCell>{parseAmount(payment.amount).toFixed(2)}</TableCell>
                        <TableCell>{payment.payment_date}</TableCell>
                        <TableCell>{payment.currency_details?.title || 'USD'}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: 'success.light', fontWeight: 'bold' }}>
                      <TableCell colSpan={2}><strong>TOTAL ({payments.length} payments)</strong></TableCell>
                      <TableCell><strong>{summary.total_payments.amount.toFixed(2)}</strong></TableCell>
                      <TableCell colSpan={2}><strong>Total Paid</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Return Tab */}
            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Purchase</TableCell>
                      <TableCell>Return Date</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Refund Amount</TableCell>
                      <TableCell>Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returns.map((returnItem) => (
                      <TableRow key={returnItem.id}>
                        <TableCell>#{returnItem.id}</TableCell>
                        <TableCell>Purchase #{returnItem.purchase_details?.id || 'N/A'}</TableCell>
                        <TableCell>{returnItem.return_date}</TableCell>
                        <TableCell>{returnItem.reason || '-'}</TableCell>
                        <TableCell>{parseAmount(returnItem.total_refund_amount || (returnItem.return_items?.[0]?.refund_amount) || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip label={returnItem.return_items?.length || 0} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ backgroundColor: 'info.light', fontWeight: 'bold' }}>
                      <TableCell colSpan={4}><strong>TOTAL ({returns.length} returns)</strong></TableCell>
                      <TableCell><strong>{summary.total_returns.amount.toFixed(2)}</strong></TableCell>
                      <TableCell><strong>{returns.reduce((sum, r) => sum + (r.return_items?.length || 0), 0)} items returned</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
